import React, {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Button, Card } from "react-bootstrap";
import { Loading } from "../../ui";
import { AdminTable } from "../admin/AdminTable";
import { EditableTextCell, TagsCell, tagsFilter } from "../admin/cells";
import { useFlash } from "../admin/Flash";
import { useSavedRows } from "../admin/useSavedRows";
import { Library } from "../library/Library";
import { LibraryService } from "../library/LibraryService";
import { ProductsNav } from "../ProductsNav";
import { Bundle } from "./Bundle";
import { BundleForm } from "./BundleForm";
import { BundleService, IBundlePatch } from "./BundleService";

const bundleService = new BundleService();

function toPatch(changes: Partial<Bundle>): IBundlePatch {
	const { libraries, ...fields } = changes;
	return libraries
		? {
				...fields,
				library_ids: libraries.map(library => library.id as number),
		  }
		: fields;
}

export const BundlePage = (): ReactElement => {
	const [libraries, setLibraries] = useState<Library[]>();
	const [showCreate, setShowCreate] = useState(false);
	const flash = useFlash();
	const { rows: bundles, setRows, statuses, save } = useSavedRows<Bundle>(
		message => flash.show(`Couldn't save: ${message}`, true),
	);

	useEffect(() => {
		bundleService.findAll().then(setRows);
		new LibraryService().findAll().then(setLibraries);
	}, []);

	const saveRow = useCallback(
		(id: number, changes: Partial<Bundle>) =>
			save([id], changes, async () => [
				await bundleService.patch(id, toPatch(changes)),
			]),
		[save],
	);

	const columns = useMemo(
		() => [
			{ Header: "ID", accessor: "id", width: "6%", disableFilters: true },
			{
				Header: "Name",
				accessor: "name",
				width: "40%",
				Cell: EditableTextCell,
				required: true,
			},
			{
				Header: "Code",
				accessor: "code",
				width: "15%",
				Cell: EditableTextCell,
				required: true,
			},
			{
				Header: "Libraries",
				accessor: "libraries",
				width: "39%",
				Cell: TagsCell,
				options: libraries || [],
				filter: tagsFilter,
				disableSortBy: true,
			},
		],
		[libraries],
	);

	if (!bundles || !libraries) {
		return <Loading />;
	}

	return (
		<div className="container mb-3" id="products-admin">
			<div className="products-page-header">
				<div className="products-page-header-left">
					<div className="products-page-icon products-page-icon-bundles">
						<i className="fa fa-cubes" />
					</div>
					<div>
						<h1>Bundles</h1>
						<span className="products-page-count">
							{bundles.length} bundle
							{bundles.length !== 1 ? "s" : ""}
						</span>
					</div>
				</div>
				<Button
					variant={showCreate ? "outline-secondary" : "primary"}
					onClick={() => setShowCreate(!showCreate)}
					className="products-create-toggle"
				>
					<i
						className={`fa ${showCreate ? "fa-times" : "fa-plus"}`}
					/>{" "}
					{showCreate ? "Cancel" : "New Bundle"}
				</Button>
			</div>

			<ProductsNav active="bundles" />

			{flash.element}

			{showCreate && (
				<Card className="mb-4 products-create-card">
					<Card.Body>
						<BundleForm
							onCreate={bundle => {
								setRows([bundle, ...bundles]);
								setShowCreate(false);
								flash.show(`"${bundle.name}" created`);
							}}
						/>
					</Card.Body>
				</Card>
			)}

			<div className="editable-data-table products-table-wrapper">
				<AdminTable
					columns={columns}
					data={bundles}
					statuses={statuses}
					saveRow={saveRow}
				/>
			</div>
		</div>
	);
};
